
/**
 * Operation Queue - Prevents concurrent operations on the same resource
 * Ensures operations that affect the same entity are executed sequentially
 */
type OperationResult<T> = {
  success: boolean;
  data?: T;
  error?: Error;
};

export class OperationQueue {
  private operations = new Map<string, Promise<any>>();
  private activeOperations = new Set<string>();
  private debug = false;
  private completedOperations: { key: string, timestamp: number }[] = [];
  private maxCompletedOperations = 50;
  private cache = new Map<string, any>();

  constructor(debug = false) {
    this.debug = debug;
  }

  /**
   * Store data in the operation cache
   */
  setCache<T>(key: string, data: T): void {
    this.cache.set(key, data);
    if (this.debug) {
      console.log(`[OperationQueue] Cached data for key: ${key}`);
    }
  }

  /**
   * Get data from the operation cache
   */
  getCache<T>(key: string): T | undefined {
    const data = this.cache.get(key) as T | undefined;
    if (this.debug && data) {
      console.log(`[OperationQueue] Found cached data for key: ${key}`);
    }
    return data;
  }

  /**
   * Check if cache has data for key
   */
  hasCache(key: string): boolean {
    return this.cache.has(key);
  }

  /**
   * Enqueues an operation for a specific resource key
   * If an operation is already in progress for this key, it will wait for it to complete
   */
  async enqueue<T>(key: string, operation: () => Promise<T>, skipIfExists = false): Promise<OperationResult<T>> {
    if (this.debug) {
      console.log(`[OperationQueue] Enqueueing operation for key: ${key}`);
      console.log(`[OperationQueue] Active operations: ${Array.from(this.activeOperations)}`);
    }

    // If skipIfExists is true, check if we already have cached data
    if (skipIfExists && this.cache.has(key)) {
      const cachedData = this.cache.get(key) as T;
      if (this.debug) {
        console.log(`[OperationQueue] Using cached data for key: ${key}`);
      }
      return { success: true, data: cachedData };
    }

    // First, check if we have a very recently completed operation for this key
    const recentlySameOperation = this.completedOperations.find(
      op => op.key === key && Date.now() - op.timestamp < 5000
    );
    
    if (recentlySameOperation && this.debug) {
      console.log(`[OperationQueue] Warning: ${key} was already processed within the last 5 seconds`);
    }

    // Check if there's an ongoing operation for this key
    const existingOperation = this.operations.get(key);
    
    // Create a new operation that waits for any existing operation to complete
    const newOperation = (async () => {
      try {
        // Wait for any existing operation to finish
        if (existingOperation) {
          if (this.debug) console.log(`[OperationQueue] Waiting for existing operation on ${key}`);
          await existingOperation;
        }
        
        // Mark this key as having an active operation
        this.activeOperations.add(key);
        
        if (this.debug) console.log(`[OperationQueue] Starting operation on ${key}`);
        
        // Execute the operation
        const result = await operation();
        
        if (this.debug) console.log(`[OperationQueue] Operation completed successfully for ${key}`);
        
        // Cache the result
        this.setCache(key, result);
        
        return { success: true, data: result };
      } catch (error) {
        if (this.debug) console.error(`[OperationQueue] Operation failed for ${key}:`, error);
        return { 
          success: false, 
          error: error instanceof Error ? error : new Error(String(error)) 
        };
      } finally {
        // Clean up when done
        this.operations.delete(key);
        this.activeOperations.delete(key);
        
        // Track completed operation
        this.completedOperations.push({ key, timestamp: Date.now() });
        if (this.completedOperations.length > this.maxCompletedOperations) {
          this.completedOperations.shift(); // Remove oldest
        }
        
        if (this.debug) console.log(`[OperationQueue] Cleanup completed for ${key}`);
      }
    })();

    // Store the new operation in our map
    this.operations.set(key, newOperation);
    
    // Return the promise for the caller to await
    return newOperation;
  }

  /**
   * Check if a key has an active operation
   */
  isActive(key: string): boolean {
    return this.activeOperations.has(key);
  }

  /**
   * Get the number of active operations
   */
  get activeCount(): number {
    return this.activeOperations.size;
  }
  
  /**
   * Get the list of active operation keys
   */
  getActiveOperations(): string[] {
    return Array.from(this.activeOperations);
  }
  
  /**
   * Get operations that start with a specific prefix
   */
  getOperationsByPrefix(prefix: string): string[] {
    return Array.from(this.activeOperations).filter(key => key.startsWith(prefix));
  }
  
  /**
   * Check if an operation was recently completed
   */
  wasRecentlyCompleted(key: string, timeWindowMs: number = 5000): boolean {
    const recentOperation = this.completedOperations.find(
      op => op.key === key && Date.now() - op.timestamp < timeWindowMs
    );
    return !!recentOperation;
  }
  
  /**
   * Get recently completed operations
   */
  getRecentlyCompleted(timeWindowMs: number = 30000): string[] {
    const cutoff = Date.now() - timeWindowMs;
    return this.completedOperations
      .filter(op => op.timestamp >= cutoff)
      .map(op => op.key);
  }
  
  /**
   * Clear the cache entry for a key
   */
  clearCache(key: string): void {
    this.cache.delete(key);
    if (this.debug) {
      console.log(`[OperationQueue] Cleared cache for key: ${key}`);
    }
  }
}

// Export a singleton instance for app-wide use
export const operationQueue = new OperationQueue(true);
